import { noop } from "@/helpers/functions/FunctionHelpers";
import { computed, readonly, shallowRef, watch } from "vue";
import {
  createEventHook,
  tryOnUnmounted,
  useIntervalFn,
  useTimeoutFn,
} from "@vueuse/core";

const STATUS = Object.freeze({
  IDLE: "idle",
  PENDING: "pending",
  SUCCESS: "success",
  ERROR: "error",
});

/**
 * @typedef {Object} DispatchWhenOptions
 * @property {number} [timeout=Infinity] Tempo máximo de execução, em milissegundos.
 * @property {number} [interval=1000] Intervalo entre os dispatches, em milissegundos.
 */

/**
 * Executa `dispatch` em intervalos regulares enquanto `observer` for `true`.
 *
 * @param {import("vue").Ref<boolean>} observer
 * @param {() => unknown | Promise<unknown>} [dispatch]
 * @param {DispatchWhenOptions} [options]
 */
export function useDispatchWhen(observer, dispatch = noop, options = {}) {
  const { interval = 1000, timeout = Infinity } = options;

  const status = shallowRef(STATUS.IDLE);
  const isPaused = shallowRef(false);
  const isDispatching = shallowRef(false);
  const lastResult = shallowRef();

  const successEvent = createEventHook();
  const errorEvent = createEventHook();
  const finishEvent = createEventHook();
  const startEvent = createEventHook();

  const isPending = computed(() => status.value === STATUS.PENDING);
  const isSuccess = computed(() => status.value === STATUS.SUCCESS);
  const isIdle = computed(() => status.value === STATUS.IDLE);
  const isError = computed(() => status.value === STATUS.ERROR);

  const pollingControls = useIntervalFn(executeDispatch, interval, {
    immediate: false,
  });

  const timeoutControls = useTimeoutFn(() => finish(STATUS.ERROR), timeout, {
    immediate: false,
  });

  function stop() {
    pollingControls.pause();
    timeoutControls.stop();
  }

  function finish(nextStatus, error) {
    if (status.value !== STATUS.PENDING) return;

    stop();
    status.value = nextStatus;

    const context = {
      status: nextStatus,
      result: lastResult.value,
    };

    try {
      if (nextStatus === STATUS.SUCCESS) {
        successEvent.trigger(lastResult.value);
      } else {
        errorEvent.trigger(error);
      }
    } finally {
      finishEvent.trigger(context);
    }
  }

  async function executeDispatch() {
    const isPending = status.value === STATUS.PENDING;
    if (!isPending || isPaused.value || isDispatching.value) return;

    if (!observer.value) return finish(STATUS.SUCCESS);

    isDispatching.value = true;

    try {
      const result = await dispatch();

      if (!isPending) return;
      lastResult.value = result;

      if (!observer.value) finish(STATUS.SUCCESS);
    } catch (error) {
      finish(STATUS.ERROR);
    } finally {
      isDispatching.value = false;
    }
  }

  function start() {
    if (!observer.value) return;
    startEvent.trigger();

    isPaused.value = false;
    status.value = STATUS.PENDING;
    pollingControls.resume();

    if (Number.isFinite(timeout)) timeoutControls.start();

    executeDispatch();
  }

  function pause() {
    isPaused.value = true;
    stop();

    const isPending = status.value === STATUS.PENDING;
    if (isPending) status.value = STATUS.IDLE;
  }

  function resume() {
    start();
  }

  function reset() {
    stop();
    isPaused.value = false;
    lastResult.value = undefined;
    status.value = STATUS.IDLE;

    if (observer.value) start();
  }

  const stopObserver = watch(
    observer,
    (shouldDispatch) => {
      if (shouldDispatch && !isPaused.value) {
        start();
        return;
      }

      if (!shouldDispatch) finish(STATUS.SUCCESS);
    },
    { immediate: true }
  );

  tryOnUnmounted(() => {
    stopObserver();
    stop();
  });

  return {
    isError,
    isIdle,
    isPending,
    isSuccess,
    status: readonly(status),
    onError: errorEvent.on,
    onFinish: finishEvent.on,
    onSuccess: successEvent.on,
    onStart: startEvent.on,
    pause,
    reset,
    resume,
  };
}
